import React, { useEffect, useState } from 'react';
import api from '../services/api';
import './InkooporderBetaalinfoCard.css';

function formatDateInput(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return s.split('T')[0] || '';
}

function moneyFromOrder(order, primaryKey, legacyKey) {
  const v = order?.[primaryKey];
  if (v != null && v !== '') return String(v);
  if (legacyKey && order?.[legacyKey] != null && order[legacyKey] !== '') return String(order[legacyKey]);
  return '';
}

/**
 * Interne betaalnotities: hoofdsom, aanbetaling en restant (USD/EUR + datums).
 */
export default function InkooporderBetaalinfoCard({ order, onOrderRefresh }) {
  const [hoofdUsd, setHoofdUsd] = useState('');
  const [hoofdEur, setHoofdEur] = useState('');
  const [aanUsd, setAanUsd] = useState('');
  const [aanEur, setAanEur] = useState('');
  const [aanDatum, setAanDatum] = useState('');
  const [restUsd, setRestUsd] = useState('');
  const [restEur, setRestEur] = useState('');
  const [restDatum, setRestDatum] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!order) return;
    setHoofdUsd(moneyFromOrder(order, 'hoofdsom_usd', null));
    setHoofdEur(moneyFromOrder(order, 'hoofdsom_eur', null));
    setAanUsd(moneyFromOrder(order, 'aanbetaling_bedrag_usd', 'betaald_bedrag_usd'));
    setAanEur(moneyFromOrder(order, 'aanbetaling_bedrag_eur', 'betaald_bedrag_eur'));
    setAanDatum(formatDateInput(order.betaaldatum_aanbetaling));
    setRestUsd(moneyFromOrder(order, 'restant_bedrag_usd', null));
    setRestEur(moneyFromOrder(order, 'restant_bedrag_eur', null));
    setRestDatum(formatDateInput(order.betaaldatum_afrekening));
  }, [
    order?.id,
    order?.hoofdsom_usd,
    order?.hoofdsom_eur,
    order?.aanbetaling_bedrag_usd,
    order?.aanbetaling_bedrag_eur,
    order?.betaald_bedrag_usd,
    order?.betaald_bedrag_eur,
    order?.restant_bedrag_usd,
    order?.restant_bedrag_eur,
    order?.betaaldatum_aanbetaling,
    order?.betaaldatum_afrekening,
  ]);

  const parseMoney = (raw) => {
    const t = String(raw ?? '').trim().replace(',', '.');
    if (!t) return null;
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : NaN;
  };

  const validateDate = (label, s) => {
    const t = String(s || '').trim();
    if (!t) return { ok: true, value: null };
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return { ok: false, error: `${label}: gebruik JJJJ-MM-DD.` };
    return { ok: true, value: t };
  };

  const save = async () => {
    if (!order?.id) return;
    const pairs = [
      ['Hoofdsom USD', hoofdUsd],
      ['Hoofdsom EUR', hoofdEur],
      ['Aanbetaling USD', aanUsd],
      ['Aanbetaling EUR', aanEur],
      ['Restant USD', restUsd],
      ['Restant EUR', restEur],
    ];
    const nums = [];
    for (const [label, raw] of pairs) {
      const n = parseMoney(raw);
      if (Number.isNaN(n)) {
        window.alert(`Vul bij "${label}" een geldig bedrag in of laat leeg.`);
        return;
      }
      nums.push(n);
    }
    const dAan = validateDate('Datum aanbetaling', aanDatum);
    const dRest = validateDate('Datum restant / afrekening', restDatum);
    if (!dAan.ok) {
      window.alert(dAan.error);
      return;
    }
    if (!dRest.ok) {
      window.alert(dRest.error);
      return;
    }

    setSaving(true);
    try {
      await api.put(`/inkooporders/${order.id}/betaalinfo`, {
        hoofdsom_usd: nums[0],
        hoofdsom_eur: nums[1],
        aanbetaling_bedrag_usd: nums[2],
        aanbetaling_bedrag_eur: nums[3],
        restant_bedrag_usd: nums[4],
        restant_bedrag_eur: nums[5],
        betaaldatum_aanbetaling: dAan.value,
        betaaldatum_afrekening: dRest.value,
      });
      const response = await api.get(`/inkooporders/${order.id}`);
      onOrderRefresh?.(response.data);
    } catch (e) {
      console.error('Betaalinfo opslaan:', e);
      window.alert('Fout bij opslaan: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  return (
    <div className="inkooporder-betaalinfo-card">
      <h4 className="inkooporder-betaalinfo-title">Betaalinfo</h4>
      <p className="inkooporder-betaalinfo-hint">Intern — niet op de PDF naar leverancier.</p>

      <section className="inkooporder-betaalinfo-section">
        <h5 className="inkooporder-betaalinfo-subtitle">Hoofdsom (order)</h5>
        <div className="inkooporder-betaalinfo-cols">
          <label>
            USD
            <input
              type="text"
              inputMode="decimal"
              value={hoofdUsd}
              onChange={(e) => setHoofdUsd(e.target.value)}
              placeholder="—"
              autoComplete="off"
            />
          </label>
          <label>
            EUR
            <input
              type="text"
              inputMode="decimal"
              value={hoofdEur}
              onChange={(e) => setHoofdEur(e.target.value)}
              placeholder="—"
              autoComplete="off"
            />
          </label>
        </div>
      </section>

      <section className="inkooporder-betaalinfo-section">
        <h5 className="inkooporder-betaalinfo-subtitle">Aanbetaling</h5>
        <div className="inkooporder-betaalinfo-cols">
          <label>
            Bedrag USD
            <input
              type="text"
              inputMode="decimal"
              value={aanUsd}
              onChange={(e) => setAanUsd(e.target.value)}
              placeholder="—"
              autoComplete="off"
            />
          </label>
          <label>
            Bedrag EUR
            <input
              type="text"
              inputMode="decimal"
              value={aanEur}
              onChange={(e) => setAanEur(e.target.value)}
              placeholder="—"
              autoComplete="off"
            />
          </label>
        </div>
        <label className="inkooporder-betaalinfo-full">
          Datum betaald
          <input type="date" value={aanDatum} onChange={(e) => setAanDatum(e.target.value)} />
        </label>
      </section>

      <section className="inkooporder-betaalinfo-section">
        <h5 className="inkooporder-betaalinfo-subtitle">Restant</h5>
        <div className="inkooporder-betaalinfo-cols">
          <label>
            Bedrag USD
            <input
              type="text"
              inputMode="decimal"
              value={restUsd}
              onChange={(e) => setRestUsd(e.target.value)}
              placeholder="—"
              autoComplete="off"
            />
          </label>
          <label>
            Bedrag EUR
            <input
              type="text"
              inputMode="decimal"
              value={restEur}
              onChange={(e) => setRestEur(e.target.value)}
              placeholder="—"
              autoComplete="off"
            />
          </label>
        </div>
        <label className="inkooporder-betaalinfo-full">
          Datum betaald / afrekening
          <input type="date" value={restDatum} onChange={(e) => setRestDatum(e.target.value)} />
        </label>
      </section>

      <button type="button" className="btn btn-primary btn-sm inkooporder-betaalinfo-save" onClick={save} disabled={saving}>
        {saving ? 'Opslaan…' : 'Betaalinfo opslaan'}
      </button>
    </div>
  );
}
